// Köposter för tomläges-CTA:n ("Hittar du ingen tid? Ställ dig i kö").
//
// VÄG A (009 GO-TILLÄGG p3): CTA:n skapar EN köpost per bana i hallen för den
// valda tiden — inte en enda rad med court_id = NULL.
//
// Varför: `waitlist.court_id` är formellt nullbar, men hela erbjudandevägen
// matchar på `.eq("court_id", …)` — auto-erbjudandet vid avbokning, `offerToAll`
// i AdminWaitlist och `waitlist-expire`. En NULL-rad hade lagrats utan problem,
// synts i adminlistan, och sedan aldrig matchats av någon automatik. Väg A
// återanvänder erbjudandevägen precis som den är, utan att röra betalvägsfilen.
//
// Priset är N rader per köad tid. Det är medvetet: hellre N rader som fungerar
// än en rad som ser rätt ut och aldrig erbjuds.

/** Bana som köposten kan gälla. */
export interface WaitlistCourt {
  id: string;
}

export interface BuildWaitlistRowsInput {
  organizationId: string;
  userId: string;
  courts: ReadonlyArray<WaitlistCourt>;
  /** Datum i hallens lokala tid. */
  date: Date;
  /** "HH:mm" — slotens starttid. */
  startTime: string;
  durationMinutes: number;
  /**
   * Köposition. Samma semantik som Book.tsx:492: rådgivande, eftersom
   * erbjudandevägen sorterar på (position, created_at).
   */
  startPosition?: number;
}

export interface WaitlistRowInsert {
  organization_id: string;
  user_id: string;
  court_id: string;
  date: string;
  start_time: string;
  end_time: string;
  position: number;
  status: "waiting";
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** "yyyy-MM-dd" ur ett Date, i lokal tid (aldrig via toISOString — den är UTC). */
export function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * "HH:mm" + minuter → "HH:mm:ss".
 *
 * Klockan tillåts passera 24:00 lika lite som hallen gör; en sluttid bortom
 * dygnet klampas till 23:59:59 hellre än att wrappa till nästa dygn, eftersom
 * `waitlist.date` och `end_time` är separata kolumner och en wrap skulle lägga
 * posten på fel datum.
 */
export function addMinutesToClock(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) throw new Error(`ogiltig tid: ${hhmm}`);
  const total = h * 60 + m + minutes;
  if (total >= 24 * 60) return "23:59:59";
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}:00`;
}

/**
 * Bygger raderna en tomläges-CTA ska skriva — en per bana.
 *
 * Tom banlista ger tom array: en köpost utan bana kan inte erbjudas, så vi
 * skriver hellre ingenting än en rad som aldrig matchas.
 */
export function buildWaitlistRows(input: BuildWaitlistRowsInput): WaitlistRowInsert[] {
  const {
    organizationId,
    userId,
    courts,
    date,
    startTime,
    durationMinutes,
    startPosition = 1,
  } = input;

  if (courts.length === 0) return [];

  const dateKey = localDateKey(date);
  const start = `${startTime}:00`;
  const end = addMinutesToClock(startTime, durationMinutes);

  return courts.map((court) => ({
    organization_id: organizationId,
    user_id: userId,
    court_id: court.id,
    date: dateKey,
    start_time: start,
    end_time: end,
    position: startPosition,
    status: "waiting" as const,
  }));
}

/**
 * Ska tomläges-CTA:n renderas?
 *
 * L21: en toggle måste ha en enforcement-läsning. Före 009 gatade
 * `waitlist_enabled` BARA adminytan (`/manager/waitlist` + sidomenyn) — spelarens
 * "Köa?"-väg i tidslinjen var helt ogatad, så en hall med flaggan AV kunde ändå
 * samla köposter som ingen admin hade en sida att se dem på. Både den nya CTA:n
 * och den gamla vägen går nu genom den här funktionen.
 *
 * Utloggade får se CTA:n (den leder till inloggning) men skapar ingen post —
 * RLS kräver `auth.uid()` och medlemskap i orgen.
 */
export function shouldShowWaitlistCta(opts: {
  featureEnabled: boolean;
  isFull: boolean;
  slotIsInPast: boolean;
}): boolean {
  return opts.featureEnabled && opts.isFull && !opts.slotIsInPast;
}
